import React from 'react';
import { array, string } from 'prop-types';
import classNames from 'classnames';
import { LuArrowRight } from 'react-icons/lu';
import { FormattedMessage } from '../../util/reactIntl';
import { createSlug } from '../../util/urlHelpers';
import { NamedLink, ResponsiveImage } from '../../components';

import css from './SectionRelatedWork.css';

// Scaled variants keep the uploaded aspect ratio; the 16:9 card crops with object-fit.
const WORK_IMAGE_VARIANTS = ['scaled-small', 'scaled-medium', 'scaled-large'];
const WORK_IMAGE_SIZES = '(max-width: 767px) 100vw, (max-width: 1023px) 50vw, 363px';

// The cover is the first image that isn't placed inside the work's body
// (dynamicImageData), as on PortfolioWorkCard.
const coverImage = work => {
  const images = work.images || [];
  const dynamicImageData = work.attributes.publicData?.dynamicImageData || [];
  const dynamicImageIds = dynamicImageData.map(d => d && d.imageId);
  return images.find(img => !dynamicImageIds.includes(img.id?.uuid || img.id)) || null;
};

// DS "Service page / Work card" 314:1944: the cover with the title on a scrim.
// Without a cover, the card shows the blue or green placeholder tint.
const WorkCard = ({ work, index }) => {
  const title = work.attributes.title || '';
  const cover = coverImage(work);
  return (
    <li className={css.item}>
      <NamedLink
        className={classNames(css.card, { [css.cardAltTint]: index % 2 === 1 })}
        name="PortfolioWorkPageCanonical"
        params={{ id: work.id.uuid, slug: createSlug(title) }}
      >
        {cover ? (
          <ResponsiveImage
            rootClassName={css.image}
            alt=""
            image={cover}
            variants={WORK_IMAGE_VARIANTS}
            sizes={WORK_IMAGE_SIZES}
          />
        ) : null}
        <span className={css.scrim} aria-hidden="true" />
        <span className={css.cardTitle}>{title}</span>
      </NamedLink>
    </li>
  );
};

// Figma 314:1952 "Related work": up to three of the author's published
// portfolio works (the picked ones or the newest, see relatedListingsForPage)
// and a link to the full portfolio. Hidden when the author has no public works.
const SectionRelatedWork = props => {
  const { works, authorId, authorName } = props;

  if (!works || works.length === 0 || !authorId) {
    return null;
  }

  return (
    <section className={css.root}>
      <div className={css.heading}>
        <div className={css.headingText}>
          <h2 className={css.title}>
            <FormattedMessage id="SectionRelatedWork.title" />
          </h2>
          <p className={css.subtitle}>
            <FormattedMessage id="SectionRelatedWork.subtitle" values={{ name: authorName }} />
          </p>
        </div>
        <NamedLink
          className={css.textLink}
          name="ProfilePage"
          params={{ id: authorId }}
          to={{ hash: '#portfolio' }}
        >
          <FormattedMessage id="SectionRelatedWork.fullPortfolio" />
          <LuArrowRight className={css.arrow} aria-hidden="true" />
        </NamedLink>
      </div>
      <ul className={css.grid}>
        {works.map((work, index) => (
          <WorkCard key={work.id.uuid} work={work} index={index} />
        ))}
      </ul>
    </section>
  );
};

SectionRelatedWork.defaultProps = {
  works: [],
  authorId: null,
  authorName: '',
};

SectionRelatedWork.propTypes = {
  works: array,
  authorId: string,
  authorName: string,
};

export default SectionRelatedWork;
